'use strict';

var urlJoin = require('url-join');
var q = require('q');
var request = require('superagent');
var cookie = require('cookie-cutter');

/**
 *  @module YoSubscriptions
 *  @description This is a simple service for fetching a user's
 *  subscriptions. There are currently two endpoints, one for
 *  grabbing all of a user's subscriptions(in an array) and another
 *  for grabbing the user's current 'package'(e.g. 'bronze', 'silver',
 *  'wl_premium', etc.). This module makes hitting those endpoints
 *  simpler by supplying two methods: `subscriptions()` and `currentPackage()`.
 *  You can supply a dictionary of options you'd like to use when creating
 *  a YoSubscriptions instance; currently the only supported option is
 *  `prefix`.
 */

function YoSubscriptions(options) {
  var origin = options.origin || '/';
  var prefix = options.prefix || '/subscriptionservice/';
  this.baseUrl = urlJoin(origin, prefix, '/pb/subscriptions/');
  this.csrfValue = cookie.get('csrftoken');
}

/** fetches all of the current user's subscriptions, returning them as an
 *  array of subscription objects.
 *  @function
 *  @name subscriptions
 *  @returns {array} a list of subscriptions(which will be empty if the
 *  current user has none).
 */

YoSubscriptions.prototype.subscriptions = function(options) {
  var deferred = q.defer();
  var opts = options || {};
  var statusIn = opts.statusIn;
  var typeIn = opts.typeIn;
  var url = this.baseUrl;
  var query = '';

  if (statusIn && statusIn.length) {
    query += '?status__in=' + statusIn.join(',');
  }

  if (typeIn && typeIn.length) {
    var typeQuery = 'type__in=' + typeIn.join(',');

    query = query ? query + '&' + typeQuery : '?' + typeQuery;
  }

  request
    .get(url + query)
    .accept('application/json')
    .withCredentials()
    .set('X-CSRFToken', this.csrfValue)
    .end(function (err, res) {
      if (err) {
        deferred.reject(err);
        return;
      }
      
      deferred.resolve(res);
    });

  return deferred.promise;
};

/** fetches the current user's current package, returning a 'package' object
 *  with all of the user/package relevant data in it.
 *  @function
 *  @name currentPackage
 *  @returns {object} currentPackage: an object holding data such as
 *  'type'(e.g. 'gold'), 'user_id', 'start_date', 'billing_date', et al.
 */

YoSubscriptions.prototype.currentPackage = function() {
  var deferred = q.defer();
  var url = urlJoin(this.baseUrl, 'current_package/');

  request
    .get(url)
    .accept('application/json')
    .withCredentials()
    .set('X-CSRFToken', this.csrfValue)
    .end(function (err, res) {
      if (err) {
        deferred.reject(err);
        return;
      }
      
      deferred.resolve(res);
    });

  return deferred.promise;
};

/** fetches the current user's current package, returning a 'package' object
 *  with all of the user/package relevant data in it. Used in Latitude applications
 *  @function
 *  @name getCurrentPackage
 *  @returns {object} currentPackage
 */

YoSubscriptions.prototype.getCurrentPackage = function() {
  var deferred = q.defer();
  var url = urlJoin(this.baseUrl, 'current-package/');

  request
    .get(url)
    .accept('application/json')
    .withCredentials()
    .set('X-CSRFToken', this.csrfValue)
    .end(function (err, res) {
      if (err) {
        deferred.reject(err);
        return;
      }
      
      deferred.resolve(res);
    });

  return deferred.promise;
};

/**
 * fetches a subscription by id
 * @function
 * @name  get
 * @param {string} subId the id of the subscription wanted
 * @returns {object} xhr object containing the subscription object with id: subId
 */

YoSubscriptions.prototype.get = function(subId) {
  var deferred = q.defer();
  var url = urlJoin(this.baseUrl, subId, '/');

  request
    .get(url)
    .accept('application/json')
    .set('X-CSRFToken', this.csrfValue)
    .end(function (err, res) {
      if (err) {
        deferred.reject(err);
        return;
      }
      
      deferred.resolve(res);
    });

  return deferred.promise;
};

/**
 * cancels a subscription by id
 * @function
 * @name  cancel
 * @param {string} subId the id of the subscription to cancel
 * @param {string} reason optional, cancelation reason
 * @returns {object} xhr object containing the subscription object with id: subId
 * which will now have a `status` of `'canceled'`
 */

YoSubscriptions.prototype.cancel = function(subId, reason) {
  var deferred = q.defer();
  var url = urlJoin(this.baseUrl, subId, 'cancel/');

  var req = request
    .post(url)
    .accept('application/json')
    .withCredentials()
    .set('X-CSRFToken', this.csrfValue);

  if (reason) {
    req = req.send({ reason: reason });
  }

  req.end(function (err, res) {
    if (err) {
      deferred.reject(err);
      return;
    }
    
    deferred.resolve(res);
  });

  return deferred.promise;
}

/**
 * cancels trial subscription by id
 * @function
 * @name  cancelTrial
 * @param {string} subId the id of the subscription to cancel trial
 * @param {string} reason cancelation reason
 * @returns {object} xhr object containing the subscription object with id: subId
 * which will now have a `status` of `'deprovisioned'`
 */

YoSubscriptions.prototype.cancelTrial = function(subId, reason) {
  var deferred = q.defer();
  var url = urlJoin(this.baseUrl, subId, 'cancel-trial/');

  var req = request
    .post(url)
    .accept('application/json')
    .withCredentials()
    .set('X-CSRFToken', this.csrfValue)

  if (reason) {
    req = req.send({ reason: reason });
  }

  req.end(function (err, res) {
    if (err) {
      deferred.reject(err);
      return;
    }

    deferred.resolve(res);
  });

  return deferred.promise;
};

/**
 * reactivate a subscription by id
 * @function
 * @name  reactivate
 * @param {string} subId the id of the subscription to reactivate
 * @returns {object} xhr object containing the subscription object with id: subId
 * which will now have a `status` of `'active'`
 */

YoSubscriptions.prototype.reactivate = function(subId) {
  var deferred = q.defer();
  var url = urlJoin(this.baseUrl, subId, 'reactivate/');

  request
    .post(url)
    .accept('application/json')
    .withCredentials()
    .set('X-CSRFToken', this.csrfValue)
    .end(function (err, res) {
      if (err) {
        deferred.reject(err);
        return;
      }
      
      deferred.resolve(res);
    });

  return deferred.promise;
};

/**
 * enables auto-renewal for subscription by id
 * @function
 * @name  reschedule
 * @param {string} subId the id of the subscription to reschedule
 * @returns {object} xhr object containing the subscription object with id: subId
 */

YoSubscriptions.prototype.reschedule = function(subId) {
  var deferred = q.defer();
  var url = urlJoin(this.baseUrl, subId, 'reschedule/');

  request
    .post(url)
    .accept('application/json')
    .withCredentials()
    .set('X-CSRFToken', this.csrfValue)
    .end(function (err, res) {
      if (err) {
        deferred.reject(err);
        return;
      }
      
      deferred.resolve(res);
    });

  return deferred.promise;
};

/** fetches ecommerce plans with all of the relevant data in it
 *  @function
 *  @name getEcommercePlans
 *  @returns {object} xhr object
 * data structure example: { ecommerce_free: { name: 'eCommerce Free', features: { multilingual: false, product_limit : 3 } } }
 */

YoSubscriptions.prototype.getEcommercePlans = function() {
  var deferred = q.defer();
  var url = urlJoin(this.baseUrl, 'ecommerce-plans/');

  request
    .get(url)
    .accept('application/json')
    .withCredentials()
    .set('X-CSRFToken', this.csrfValue)
    .end(function (err, res) {
      if (err) {
        deferred.reject(err);
        return;
      }
      
      deferred.resolve(res);
    });

  return deferred.promise;
};

var yosubs = function(options) {
  return new YoSubscriptions(options || {});
};

module.exports = yosubs;
